// Notification Service - Event-based notification system for the application

export type NotificationType = 'success' | 'info' | 'warning' | 'error';

export interface NotificationPayload {
  type: NotificationType;
  title: string;
  message: string;
  autoClose?: boolean; // Auto close after duration (default: true for success/info)
  duration?: number; // Duration in ms (default: 5000)
}

export interface Notification extends NotificationPayload {
  id: number;
  time: string;
  read: boolean;
}

// Event name for notification events
const NOTIFICATION_EVENT = 'app:notification';

// Generate unique ID
let notificationIdCounter = Date.now();
const generateId = (): number => ++notificationIdCounter;

// Format time as relative string
const formatTime = (): string => 'Just now';

// Create and dispatch a notification event
const dispatchNotification = (payload: NotificationPayload): Notification => {
  const notification: Notification = {
    ...payload,
    id: generateId(),
    time: formatTime(),
    read: false,
  };

  const event = new CustomEvent<Notification>(NOTIFICATION_EVENT, {
    detail: notification,
  });

  window.dispatchEvent(event);
  return notification;
};

// Notification service object with methods for each type
export const notificationService = {
  // Show success notification
  success: (title: string, message: string, options?: Partial<NotificationPayload>): Notification => {
    return dispatchNotification({
      type: 'success',
      title,
      message,
      autoClose: true,
      duration: 5000,
      ...options,
    });
  },

  // Show info notification
  info: (title: string, message: string, options?: Partial<NotificationPayload>): Notification => {
    return dispatchNotification({
      type: 'info',
      title,
      message,
      autoClose: true,
      duration: 5000,
      ...options,
    });
  },

  // Show warning notification
  warning: (title: string, message: string, options?: Partial<NotificationPayload>): Notification => {
    return dispatchNotification({
      type: 'warning',
      title,
      message,
      autoClose: false,
      ...options,
    });
  },

  // Show error notification
  error: (title: string, message: string, options?: Partial<NotificationPayload>): Notification => {
    return dispatchNotification({
      type: 'error',
      title,
      message,
      autoClose: false,
      ...options,
    });
  },

  // Generic notify method
  notify: (payload: NotificationPayload): Notification => {
    return dispatchNotification(payload);
  },

  // Subscribe to notification events (used by main_layout)
  subscribe: (callback: (notification: Notification) => void): (() => void) => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<Notification>;
      callback(customEvent.detail);
    };

    window.addEventListener(NOTIFICATION_EVENT, handler);

    // Return unsubscribe function
    return () => {
      window.removeEventListener(NOTIFICATION_EVENT, handler);
    };
  },

  // =========================================================================
  // TOAST — Visible auto-dismissing snackbar toasts
  // =========================================================================
  toast: {
    _show(type: NotificationType, message: string, duration = 4000) {
      // Remove existing toast if any
      const existing = document.getElementById('app-toast');
      if (existing) existing.remove();

      const colors: Record<NotificationType, { bg: string; icon: string }> = {
        success: { bg: '#2563eb', icon: '✓' },
        error:   { bg: '#dc2626', icon: '✕' },
        warning: { bg: '#d97706', icon: '!' },
        info:    { bg: '#2563eb', icon: 'i' },
      };
      const c = colors[type];

      const toast = document.createElement('div');
      toast.id = 'app-toast';
      toast.innerHTML = `
        <div style="
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: ${c.bg};
          color: #ffffff;
          padding: 1rem 1.5rem;
          border-radius: 0.625rem;
          box-shadow: 0 8px 32px rgba(0,0,0,0.25);
          z-index: 99999;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          min-width: 320px;
          max-width: 500px;
          animation: toastFadeIn 0.3s ease-out;
        ">
          <span style="
            width: 1.75rem; height: 1.75rem;
            border-radius: 50%;
            background: rgba(255,255,255,0.25);
            color: white;
            display: flex; align-items: center; justify-content: center;
            font-size: 0.85rem; font-weight: 700;
            flex-shrink: 0;
          ">${c.icon}</span>
          <span style="flex: 1; line-height: 1.4; font-weight: 500;">${message}</span>
          <button onclick="document.getElementById('app-toast')?.remove()" style="
            background: none; border: none; cursor: pointer;
            color: rgba(255,255,255,0.7); font-size: 1.25rem; padding: 0; line-height: 1;
          ">×</button>
        </div>
        <style>
          @keyframes toastFadeIn {
            from { transform: translate(-50%, -50%) scale(0.9); opacity: 0; }
            to   { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          }
        </style>
      `;
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), duration);
    },

    success(message: string, duration?: number) {
      notificationService.toast._show('success', message, duration);
    },
    error(message: string, duration = 6000) {
      notificationService.toast._show('error', message, duration);
    },
    warning(message: string, duration = 5000) {
      notificationService.toast._show('warning', message, duration);
    },
    info(message: string, duration?: number) {
      notificationService.toast._show('info', message, duration);
    },
  },
};

// Default export
export default notificationService;
